import moment from "moment";

export const hostname =
  typeof window !== 'undefined' ? window.location.hostname : '';

export const isLocalhost =
  hostname === 'localhost' ||
  hostname === '127.0.0.1';

export const isDev =
    hostname === 'app.analystprep.dev';

export const isDevEnv = isLocalhost || isDev;

export const isStringEmpty = (str) => {
    try {
        const testEmpty = (value) => {
            const invisible = /[\u200B-\u200D\u2060\uFEFF]/g; // ZWSP, ZWNJ, ZWJ, WORD JOINER, BOM
            const cleaned = value.replace(invisible, '').trim();
            return cleaned.length === 0;
        }
        if (str == null) return true;
        if (typeof str === 'string' && ['undefined', 'null'].includes(str.trim())) return true;
        if (typeof str === 'number') return Number.isNaN(str); //if is number simple check isNaN
        if (str instanceof String) return testEmpty(str.valueOf());
        if (typeof str === 'string') return testEmpty(str);
        return false;
    } catch (e) {
        return true;
    }
}

//Fast test isStringEmpty test function
export const testEmptyString = () => {
    const cases = [
        [undefined, true, 0],
        [null, true, 1],
        ['', true, 2],
        ['   ', true, 3],
        ['\t\n', true, 4],
        ['\u00A0', true, 5], // NBSP
        ['\u200B', true, 6], // ZWSP
        [new String(''), true, 7],
        ['0', false, 8],
        [0, false, 9],
        ['text', false, 10],
        ['null', true, 11],
        ['null ', true, 12],
        ['undefined', true, 13],
        ['undefined ', true, 14],
        ['this is some long test \n with space \t\n ', false, 14],
        [`this is some 
            long test template & with space \t\n
              what can go wrong here?  
        `, false, 15],
        [String.fromCharCode(0x200B), true, 16],
        [String("0x00000EB"), false, 17],
        [0x00000EB, false, 18],
    ];

    try {
        for (let i = 0; i < cases.length; i++) {
            const [v, expected, num] = cases[i];
            const ise = isStringEmpty(v);
            console.log("test:: #[", num, "] >[", JSON.stringify(v), "] ise: [", ise, "] expected: [", expected, "] assert: ", expected === ise)
        }
    } catch (e) {
        console.log("Err::00", e)
    }

}

export const formatMoney = (n) => {
    const num = Number(n || 0);
    const v = isNaN(num) ? 0 : num;
    return "$" + v.toFixed(2);
}

export const formatPrettyDate = (value) => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.format("MMMM D, YYYY") : null;
}

export const formatMinutesToHHMMSS = (totalMinutes, twoDigits = false) => {
    // Calculate total seconds first (handle potential decimal minutes)
    const totalSeconds = Math.floor(totalMinutes * 60);

    // Calculate hours, minutes, and remaining seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Function to add leading zeros to single-digit numbers
    function padToTwoDigits(num) {
        if (twoDigits) {
            return num.toString().padStart(2, '0');
        }
        return num;
    }
    return {
        hours: padToTwoDigits(hours),
        minutes: padToTwoDigits(minutes),
        seconds: padToTwoDigits(seconds),
    };
}

export const capitalizeFirstLetter = (string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const stringToColor = (str) => {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash |= 0; // 32-bit int
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 65;
    const lightness = 55;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}


export const hashString = (str) => {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash |= 0;
    }

    return Math.abs(hash);
}

export const hslToRgb = (h, s, l) => {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) {
        r = c; g = x; b = 0;
    } else if (h < 120) {
        r = x; g = c; b = 0;
    } else if (h < 180) {
        r = 0; g = c; b = x;
    } else if (h < 240) {
        r = 0; g = x; b = c;
    } else if (h < 300) {
        r = x; g = 0; b = c;
    } else {
        r = c; g = 0; b = x;
    }

    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    };
}

export const rgbToHex = ({ r, g, b }) => {
    return (
        "#" +
        [r, g, b]
            .map(v => v.toString(16).padStart(2, "0"))
            .join("")
    );
}

export const getLuminance = ({ r, g, b }) => {
    const toLinear = (v) => {
        v /= 255;
        return v <= 0.03928
            ? v / 12.92
            : Math.pow((v + 0.055) / 1.055, 2.4);
    };

    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export const getContrast = (rgb1, rgb2) => {
    const l1 = getLuminance(rgb1);
    const l2 = getLuminance(rgb2);

    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return (lighter + 0.05) / (darker + 0.05);
}

export const tagToChipColors = (tag) => {
    const hash = hashString(tag);

    const hue = hash % 360;

    // Мягкий фон
    const bg = hslToRgb(hue, 55, 88);

    // Базовый тёмный текст
    let text = hslToRgb(hue, 45, 28);

    // Если вдруг контраст недостаточный — затемняем текст
    if (getContrast(bg, text) < 4.5) {
        text = hslToRgb(hue, 55, 20);
    }

    // Можно добавить бордер чуть темнее фона
    const border = hslToRgb(hue, 35, 75);

    return {
        background: rgbToHex(bg),
        color: rgbToHex(text),
        border: rgbToHex(border),
    };
}

export const isSubscriptionItem = (item) => {
    if (!item) return false;
    const pi = item.payment_interval;
    // Recurring subscriptions only (month, year, day). "lifetime" is a one-time purchase.
    return pi !== null && pi !== undefined && pi !== "" && pi !== "lifetime";
}

export const hasSubscriptionRegularMix = (items = []) => {
    if (items.length < 2) return false;
    let hasSub = false;
    let hasRegular = false;
    for (let i = 0; i < items.length; i++) {
        if (isSubscriptionItem(items[i])) hasSub = true;
        else hasRegular = true;
        if (hasSub && hasRegular) return true;
    }
    return false;
}

/**
 * Returns true when the backend cbtMockSubscriptionMiddleware has already
 * granted mock access — used as a fallback alongside legacy frontend
 * checkCfa*Subscription / checkFrm helpers whose hardcoded allowlists miss
 * newer mock-unlimited plan ids (e.g. 66, 74, 77).
 *
 * @param {*} hasMockSubscription  Response field from /checkout_v1/getExamsbyUser.
 *   Shapes:
 *     - undefined / null / false → no access
 *     - true                     → access (non-FRM boolean shape)
 *     - { frm, frm_2, frm_1_2 }  → FRM per-exam map (object shape)
 * @param {string|number} [mockLevel]  FRM-only: '1' checks frm, '2' checks frm_2.
 *   Always grants if frm_1_2 is true. If omitted, any truthy key grants access
 *   (CFA semantics — backend never returns the object shape for CFA users).
 */
export const hasMockAccessFromBackend = (hasMockSubscription, mockLevel) => {
    if (!hasMockSubscription) return false;
    if (typeof hasMockSubscription === 'boolean') return hasMockSubscription;

    // Object shape (FRM).
    if (mockLevel !== undefined && mockLevel !== null) {
        const level = String(mockLevel);
        return (
            (level === '1' && !!hasMockSubscription['frm']) ||
            (level === '2' && !!hasMockSubscription['frm_2']) ||
            !!hasMockSubscription['frm_1_2']
        );
    }
    return Object.values(hasMockSubscription).some(Boolean);
};


// WEBPACK FOOTER //
// ./src/utils/CommonUtils.js